<template>
  <div class="p-2 advanced-table-wrapper">
    <div class="table-area">
      <div id="catalog-table-wrapper" class="table-wrapper table-scroll" :style="[{ ...columnSizeVars() }]" v-if="getRowsCount">
        <table class="advanced-table">
          <thead>
            <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :style="[{ ...getCommonPinningStyles(header) }]"
                :class="[
                  { pinned: header.column.getIsPinned() },
                  { listPrice: header.column.columnDef.accessorKey === 'listPrice' }
                ]"
                :colSpan="header.colSpan">
                <FlexRender v-if="!header.isPlaceholder" :render="header.column.columnDef.header" :props="header.getContext()" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in visibleRows"
              :key="row.id"
              :class="[{ selected: row.getIsSelected() }, { disabled: !row.getCanSelect() }]"
            >
              <template v-for="cell in row.getVisibleCells()" :key="cell.id">
                <td
                  v-if="shouldRenderCell(row, cell)"
                  :style="{ ...getCommonPinningStyles(cell) }"
                  :rowSpan="getCellRowSpan(row, cell)"
                >
                  <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination bottom-pagination">
        <Pagination
          v-if="table.getPageCount() > 1"
          :pageCount="table.getPageCount()"
          :pageRange="5"
          :clickHandler="paginationClickHandler"
          :firstLastButton="true"
          containerClass="button-group"
          prevLinkClass="border left rounded p-1"
          pageLinkClass="border rounded p-1"
          nextLinkClass="border right rounded p-1"
          :marginPages="0"
          :invalidPageNumbers="getInvalidPageNumbers()"
          :value="table.getState().pagination.pageIndex"
        >
        </Pagination>
        <div class="per-page-wrapper" v-if="table.getRowCount() > 10">
          <span class="per-page-text">Her Sayfada</span>
          <SimpleSelect
            :simpleSelectItems="pageSizes"
            :selectedKey="table.getState().pagination.pageSize"
            openMenuPosition="top-left"
            @btnItemClick="sizerClickHandler(Number($event))"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  FlexRender,
  getCoreRowModel,
  useVueTable,
  getExpandedRowModel,
  createColumnHelper,
  getSortedRowModel,
  getPaginationRowModel
} from '@tanstack/vue-table'
import { SimpleSelect } from '@n11/so-ui-components'
import { ref, computed, h, watch } from 'vue'
import Label from "@/components/DataTable/Label"
import Pagination from './Pagination'

const props = defineProps({
  columns: {
    type: Array,
    required: true
  },
  attributes: {
    type: Array,
    required: true
  },
  variants: {
    type: Array,
    required: true
  },
  selectedCurrency: {
    type: Object,
    required: true,
    default: () => ({})
  }
})

const table = ref(null)
const isProductWithoutVariant = ref(null)
const data = ref([])
const rawData = ref([])
const boostedGroup = ref(null)
const INITIAL_PAGE_INDEX = 0
const INITIAL_PAGE_SIZE = 10

const pageSizes = {
  10: '10 Ürün',
  25: '25 Ürün',
  50: '50 Ürün',
  100: '100 Ürün'
}

const pagination = ref({
  pageIndex: INITIAL_PAGE_INDEX,
  pageSize: INITIAL_PAGE_SIZE
})

const externalStates = ref({})
const columnPinning = ref({
  left: ['select-col', 'name', 'images'],
  right: ['vatRate', 'actions']
})

const compareOrder = (a, b) => {
  const oa = String(a?.order ?? '')
  const ob = String(b?.order ?? '')
  const splitLast = (s) => {
    const i = s.lastIndexOf('-')
    return i === -1 ? [s, ''] : [s.slice(0, i), s.slice(i + 1)]
  }
  const [pa, sa] = splitLast(oa)
  const [pb, sb] = splitLast(ob)

  if (boostedGroup.value) {
    const aBoost = pa === boostedGroup.value
    const bBoost = pb === boostedGroup.value
    if (aBoost !== bBoost) return aBoost ? -1 : 1
  }

  const byGroup = String(pa).localeCompare(String(pb))
  if (byGroup !== 0) return byGroup

  const na = Number.parseInt(sa, 10)
  const nb = Number.parseInt(sb, 10)
  if (Number.isFinite(na) && Number.isFinite(nb)) return nb - na

  return String(sa).localeCompare(String(sb))
}
const columnHelper = createColumnHelper()

const updateValues = (values, patches = []) => {
  const { pageIndex, pageSize } = pagination.value

  const patchMap = new Map((patches || []).map(r => [r.id, r]))
  const merged = (values || []).map(v => (patchMap.has(v.id) ? { ...v, ...patchMap.get(v.id) } : v))

  const sorted = [...merged].sort(compareOrder)


  rawData.value = sorted

  const start = pageIndex * pageSize
  const end = Math.min(start + pageSize, sorted.length)
  const page = sorted.slice(start, end).map(row => ({
    ...row,
    attributes: (row.attributes || []).map(a => ({ ...a, pRowSpan: 1 })) // default 1
  }))

  const isSlicerByName = new Map(
    (props.attributes || []).map(a => [a.attributeName, !!a.isSlicer])
  )
  const attrNames = new Set(page.flatMap(r => (r.attributes || []).map(a => a.name)))
  const keyOf = (val) => (val == null ? 'null' : (typeof val === 'object' ? JSON.stringify(val) : String(val)))

  for (const name of attrNames) {
    if (!isSlicerByName.get(name)) continue
    let i = 0
    while (i < page.length) {
      const getAttr = (row) => (row.attributes || []).find(a => a.name === name)
      const baseKey = keyOf(getAttr(page[i])?.value)
      let j = i
      while (j < page.length && keyOf(getAttr(page[j])?.value) === baseKey) j++
      const span = j - i
      for (let k = i; k < j; k++) {
        const idx = page[k].attributes.findIndex(a => a.name === name)
        if (idx !== -1) {
          page[k].attributes[idx].pRowSpan = (k === i) ? span : 0
        }
      }
      i = j
    }
  }

  for (const row of page) {
    let set = false
    for (const attr of row.attributes || []) {
      if (isSlicerByName.get(attr.name)) {
        row.imagesRowSpan = typeof attr.pRowSpan === 'number' ? attr.pRowSpan : 1
        set = true
        break
      }
    }
    if (!set) row.imagesRowSpan = 1
  }

  return page
}

watch(() => props.variants, (newList) => {
  const prevMap = new Map((rawData.value || []).map(r => [r.id, r]))
  const merged = (newList || []).map(n => {
    const p = prevMap.get(n.id)
    if (!p) return n
    const out = { ...p }
    for (const [k, v] of Object.entries(n)) {
      if (v !== null && v !== undefined) out[k] = v
    }
    if ('attributes' in n) out.attributes = n.attributes
    if (p.validations || n.validations) {
      out.validations = { ...(p.validations || {}), ...(n.validations || {}) }
    }
    return out
  })

  const updatedData = updateValues(merged, data.value) || []
  if (JSON.stringify(data.value) !== JSON.stringify(updatedData)) {
    data.value = updatedData
  }

  const isSingleRowNoAttributes = updatedData.length === 1 && updatedData[0].attributes.length === 0
  const newExternalState = isSingleRowNoAttributes
    ? {
        columnVisibility: { actions: false },
        columnPinning: { left: columnPinning.value.left, right: [] }
      }
    : { columnPinning: columnPinning.value }
  if (JSON.stringify(externalStates.value) !== JSON.stringify(newExternalState)) {
    externalStates.value = newExternalState
  }
}, { immediate: true })

const paginationClickHandler = (pageNum) => {
  table.value.setPageIndex(pageNum)
  pagination.value.pageIndex = pageNum
  data.value = [...updateValues(rawData.value)]
}

const getInvalidPageNumbers = () => {
  const data = rawData.value;
  const { pageSize } = pagination.value;

  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  if (typeof pageSize !== 'number' || pageSize <= 0) {
    console.error("Page size must be a positive number.");
    return [];
  }

  const invalidPages = new Set();

  data.forEach((item, index) => {
    let itemIsInvalid = false;
    if (item.validations && typeof item.validations === 'object') {
      for (const v of Object.values(item.validations)) {
        if (v && typeof v === 'object' && v.error === true) {
          itemIsInvalid = true;
          break;
        }
      }
    }
    const pageNumber = Math.floor(index / pageSize) + 1;
    if (itemIsInvalid) {
      invalidPages.add(pageNumber);
    }
  });

  return Array.from(invalidPages).sort((a, b) => a - b);
}

const sizerClickHandler = (pageNum) => {
  table.value.setPageSize(pageNum)
  pagination.value.pageSize = pageNum
  data.value = [...updateValues(rawData.value)]
}

const visibleRows = computed(() => {
  if (table.value) {
    return table.value.getRowModel().rows || []
  }
  return []
})

const getRowsCount = computed(() => data.value?.length)

const getColumns = computed(() => {
  const baseColumns = props.columns
  const currentData = data.value

  isProductWithoutVariant.value = currentData.some(item => item.withoutVariant)
  if (isProductWithoutVariant.value) return currentData

  const dynamicColumnNames = (props.attributes || []).map(a => a.attributeName)

  const dynamicColumns = dynamicColumnNames.map(name =>
    columnHelper.accessor(name, {
      header: () => name,
      size: 164,
      noVariantsSize: 0,
      cell: ({ cell, row }) => {
        const accessorKey = cell.column.columnDef.accessorKey
        const attribute = row.original.attributes.find((a) => a.name === accessorKey)
        return h(Label, { content: attribute?.value || '' })
      }
    })
  )

  const updatedColumns = [
    ...baseColumns.slice(0, 2),
    ...dynamicColumns,
    ...baseColumns.slice(2)
  ]

  return currentData.length ? updatedColumns : []
})

table.value = useVueTable({
  get data() {
    return data.value.length ? data.value : []
  },
  get pageCount() {
    const { pageSize } = pagination.value
    return Math.ceil(props.variants.length / pageSize) ?? -1
  },
  get rowCount() {
    return props.variants.length
  },
  get columns() {
    return getColumns.value
  },
  initialState: {
    columnPinning,
    ...externalStates.value
  },
  get state() {
    return {
      pagination: pagination.value,
      ...externalStates.value
    }
  },
  getRowId: row => row.id,
  getCoreRowModel: getCoreRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  manualPagination: true,
  meta: {
    addRow: (variant) => {
      let oldData = []
      if (data.value) {
        oldData = [...data.value]
      }
      data.value = [variant, ...oldData]
    },
    updateRow: (id, columnId, value) => {
      if (data.value) {
        const updatedData = data.value.map((row) => {
          if (row.id === id) {
            return {
              ...row,
              [columnId]: value
            }
          }
          return row
        })
        data.value = updatedData;

        let targetRow = null
        rawData.value = rawData.value.map((row) => {
          if (row.id === id) {
            const next = {
              ...row,
              [columnId]: value
            }
            targetRow = next
            return next
          }
          return row
        })

        if (columnId === 'images' && targetRow) {
          const isSlicer = (name) =>
            (props.attributes || []).some(a => a.attributeName === name && a.isSlicer)

          const sameGroup = (a, b) => {
            const as = (a.attributes || []).filter(x => isSlicer(x.name))
            for (const ax of as) {
              const bv = (b.attributes || []).find(y => y.name === ax.name)?.value
              const av = ax.value
              if (JSON.stringify(av) !== JSON.stringify(bv)) return false
            }
            return true
          }

          rawData.value = rawData.value.map(r =>
            sameGroup(r, targetRow) ? { ...r, images: value } : r
          )
        }

        data.value = updateValues(rawData.value)
      }
    },
    removeRow: (rowId) => {
      rawData.value = [...rawData.value.filter(item => item.id !== rowId)]
      data.value = [...data.value.filter(item => item.id !== rowId)]
    },
    getColumnDataExceptThis: (columnName, id) => {
      const columnData = data.value.map((row) => {
        return row.id === id ? null : row[columnName]
      })
      return columnData.filter((col) => col !== null)
    },
    getData: () => {
      return data.value
    },
    getRawData: () => {
      return rawData.value
    },
    order: () => {
      data.value = data.value.sort(compareOrder)
      return data.value
    }
  }
})

const getRowSpan = (row, cell) => {
  const key = cell.column.columnDef.accessorKey
  if (key === 'images') {
    return row.original.imagesRowSpan
  }
  return row.original.attributes.find((a) => a.name === key)?.rowSpan
}

const getCellKey = (cell) =>
  cell?.column?.columnDef?.accessorKey ?? cell?.column?.id

const getCommonPinningStyles = (column) => {
  const isPinned = column.column.getIsPinned()
  const isLastLeftPinnedColumn = isPinned === 'left' && column.column.getIsLastColumn('left')
  const isFirstRightPinnedColumn = isPinned === 'right' && column.column.getIsFirstColumn('right')

  return {
    boxShadow: isLastLeftPinnedColumn
      ? '-4px 0 4px -4px gray inset'
      : isFirstRightPinnedColumn
        ? '4px 0 4px -4px gray inset'
        : undefined,
    left: isPinned === 'left' ? `${column.column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.column.getAfter('right')}px` : undefined,
    opacity: isPinned ? 0.95 : 1,
    position: isPinned ? 'sticky' : 'relative',
    width: column.column.getSize(),
    zIndex: isPinned ? 1 : 'unset',
    minWidth: `calc(var(--col-${column.column.id}-size) * 1px)`,
    maxWidth: !isPinned ? `calc(var(--col-${column.column.id}-size) * 1px)` : 'auto',
    height:'90px'
  }
}

const columnSizeVars = () => {
  const headers = table.value.getFlatHeaders()
  const colSizes = {}

  for (let i = 0; i < headers.length; i++) {
    const header = headers[i]
    colSizes[`--header-${header.id}-size`] = header.getSize()
    colSizes[`--col-${header.column.id}-size`] = header.column.getSize()
  }

  return colSizes
}

const getSelectedRows = () => {
  return table.value.getState().rowSelection
}

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? `(${value.length} items)` : 'No Data'
  }
  return value || 'N/A'
}

const getSelectedRowIds = () => {
  const sel = (table.value?.getState().rowSelection) || {}
  return Object.keys(sel).filter(k => sel[k]).map(k => String(k))
}

const applyBulkToIds = (ids, updates) => {
  const set = new Set((ids || []).map(String))
  rawData.value = (rawData.value || []).map(r => {
    if (!set.has(String(r.id))) return r
    return { ...r, ...updates }
  })
  data.value = updateValues(rawData.value)
}

const getCellRowSpan = (row, cell) => {
  const key = getCellKey(cell)
  if (key === 'images') {
    return row.original.imagesRowSpan ?? 1
  }
  const attr = row.original.attributes?.find(a => a.name === key)
  return typeof attr?.pRowSpan === 'number' ? attr.pRowSpan : 1
}

const shouldRenderCell = (row, cell) => {
  const key = getCellKey(cell)
  if (key === 'images') {
    return (row.original.imagesRowSpan ?? 1) !== 0
  }
  const attr = row.original.attributes?.find(a => a.name === key)
  return (attr?.pRowSpan ?? 1) !== 0
}

defineExpose({
  table,
  columnSizeVars,
  getCommonPinningStyles,
  getRowSpan,
  getSelectedRows,
  visibleRows,
  sizerClickHandler,
  formatValue,
  getSelectedRowIds,
  applyBulkToIds,
  pageSizes,
  paginationClickHandler,
  getRowsCount,
  getInvalidPageNumbers,
  getCellRowSpan,
  shouldRenderCell,
  boostGroup: (prefix) => { boostedGroup.value = prefix },
  clearBoost: () => { boostedGroup.value = null },
})
</script>


<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.advanced-table-wrapper {
  display: flex;
  gap: 24px;
  width: 100%;
  flex-direction: column;
  font-family: 'Open Sans';
  position: relative;
  .table-area {
    position: relative;
    z-index: 0;
  }

  .bottom-pagination {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-top: 24px;
    .per-page-wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      line-height: 16px;
      flex-shrink: 0;
      color: $colorN80;
      margin-left: auto;
    }
  }
}

.table-wrapper {
  overflow-x: scroll;
  width: 100%;
  position: relative;
  border: 1px solid $colorN50;
  border-radius: 8px;
  border-spacing: 0;
  border-collapse: separate;
  overflow-y: auto;
  overscroll-behavior: auto;

  td[rowspan="0"] {
    display: none;
  }

  .advanced-table {
    th:first-child {
      display: flex;
      justify-content: center;
      padding: 15px;
    }

    th:not(:first-child),
    td:not(:first-child) {
      width: 160px !important;
      max-width: 160px !important;
    }
    th, td {
      box-shadow: none !important;
    }
  }

  .advanced-table td :deep(input::-webkit-outer-spin-button),
  .advanced-table td :deep(input::-webkit-inner-spin-button)
   {
    -webkit-appearance: none;
    margin: 0;
  }

  &.table-scroll::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &.table-scroll::-webkit-scrollbar {
    width: 4px;
    height: 4px;
    background-color: transparent;
  }

  &.table-scroll::-webkit-scrollbar-thumb {
    background-color: $colorM50;
    border-radius: 8px;
  }

  .prices {
    max-height:18px !important;
    overflow: visible !important;
  }
}

.advanced-table {
  width: 100%;
  border-radius: 8px;
  border-collapse: separate;

  thead {
    background: $colorD10;
    color: $colorN80;

    tr {
      height: 50px;

      th {
        padding: 0px 8px 0px 12px;
        text-align: left;
        vertical-align: middle;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        color: $colorN80;
        background-color: $colorBe20;
        border-bottom: 2px solid $colorN50;
        position: sticky !important;
        top: 0;
        z-index: 2 !important;

        &.listPrice {
          z-index: 3 !important;
        }


        &.pinned {
          z-index: 3 !important;
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN50;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        background-color: $colorN00;
        height: 60px;
        vertical-align: middle;

        > {
          div {
            margin: auto;
          }

          label {
            display: block;
            margin: 0 12px;
          }

          .product-info {
            width: 244px;
          }

          .popover-wrapper {
            padding: 0 12px;
          }

          .button-wrapper,
          .status-wrapper {
            margin: 0 12px;
          }

          .checkbox {
            display: flex;
            justify-content: center;
          }
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN50;
        }
      }

      &.selected {
        td:not([rowSpan]) {
          background-color: $colorM05;
        }
      }

      &:not(:last-child) {
        td {
          border-bottom: 1px solid $colorN50;
        }
      }

      &:last-child {
        td :deep(.tooltip-text) {
          top: 60px !important;
        }
      }
    }
  }
}
</style>
